import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { DBDef_Action, DBDef_OrderGroup, DispositionTypes, pathwayType_SingleState } from '@app/pathway-shared';
import { ModuleFE_LogTemplate_OLD } from '@app/hcs-frontend';
import { filterDuplicates, ImplementationMissingException, MUSTNeverHappenException } from '@nu-art/ts-common';
import { Renderer_Transition } from '../../../../renderers/Renderer_Transition.js';
import { Empty_PathwayTransition } from '../../../../consts.js';
import { Header_Pathway } from '../../../../headers/Header_Pathway.js';
import { Header_PathwayState } from '../../../../headers/Header_PathwayState.js';
import { DataGrid, Dialog_Confirmation } from '@app/styles';
import { ModuleFE_Action, ModuleFE_OrderGroup, ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../../../../../_entity.js';
import { basicSyncEventRefresh, canEditPathway } from '../../../../utils.js';
import { Section_StateTransitions } from './panel-sections/Section_StateTransitions.js';
import { Section_AllEntityVariables } from '../../common-components/Section_AllEntityVariables.js';
import { ModuleFE_PathwayEditor } from '../../../../../../modules/index.js';
import { Renderer_PathwayItemChildSelector } from '../../../../renderers/Renderer_PathwayItemChildSelector.js';
export class Renderer_PathwayStateDetails extends ComponentSync {
    // ######################## Lifecycle ########################
    __onPathwayStateUpdated(...params) {
        const pathwayStateId = this.state.pathwayState.get('_id');
        if (!pathwayStateId)
            return;
        basicSyncEventRefresh(pathwayStateId, params, () => this.props.functions.selectPathwayState(pathwayStateId), () => this.props.functions.deselectPathwayState());
    }
    deriveStateFromProps(nextProps, state) {
        state.pathwayState = nextProps.pathwayState;
        state.pathwayIdsToDeletes ??= [];
        return state;
    }
    // ######################## Logic ########################
    deleteState = async () => {
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!pathwayId)
            throw new MUSTNeverHappenException('cannot delete state when no pathway is in context');
        // add current pathway id
        this.state.pathwayIdsToDeletes.push(pathwayId);
        Dialog_Confirmation.show({
            header: 'Are you sure?',
            content: this.renderDeleteDialog,
            buttons: [
                {
                    label: 'Yes, Delete',
                    isAsync: true,
                    action: async () => {
                        const stateId = this.props.pathwayState.item._id;
                        if (!stateId)
                            throw new MUSTNeverHappenException('Trying to delete a pathway state with no _id');
                        await ModuleFE_PathwayState._v1.deletePathwayState({
                            pathwayStates: [this.props.pathwayState.item],
                            pathwayIds: this.state.pathwayIdsToDeletes
                        }).executeSync();
                        this.props.functions.deselectPathwayState();
                    }
                },
                {
                    label: 'Don\'t Delete',
                    isPreferred: true,
                    action: () => {
                    }
                }
            ]
        });
    };
    getConnectedTransitions = () => {
        const stateId = this.props.pathwayState.item._id;
        if (!stateId)
            return [];
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!pathwayId)
            throw new MUSTNeverHappenException('cannot render this page without a selected pathway');
        return ModuleFE_PathwayTransition.cache.filter(pt => [pt.sourceId, pt.targetId].includes(stateId) && pt.pathwayIds.includes(pathwayId));
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'match_parent pathway-state-details', style: { padding: '30px', gap: '10px' }, children: [this.renderHeader(), this.renderBody()] });
    }
    renderDeleteDialog = () => {
        const transitions = this.getConnectedTransitions();
        return _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to delete the state" }), _jsx("div", { children: this.props.pathwayState.item.name }), transitions.length !== 0 && _jsxs(_Fragment, { children: [_jsx("div", { children: "By deleting this state, the following transitions will also be deleted" }), transitions.map(transition => {
                            return _jsx(Renderer_Transition, { transition: transition }, transition._id);
                        })] }), _jsxs("div", { children: ["Are you sure you want to delete this state", transitions.length ? ' and its transitions' : '', "?"] }), _jsx(Renderer_PathwayItemChildSelector, { pathwayId: this.state.pathwayIdsToDeletes[0], childFilter: child => this.state.pathwayState.get('pathwayIds').includes(child._id), onChangeCallback: (_pathwayIds) => {
                        this.setState({ pathwayIdsToDeletes: filterDuplicates([this.state.pathwayIdsToDeletes[0], ..._pathwayIds]) });
                    } })] });
    };
    // ######################## Render - Header ########################
    renderHeader = () => {
        const pathwayState = this.props.pathwayState.item;
        const viewedPathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!viewedPathwayId)
            throw new MUSTNeverHappenException('cannot render this component without selected pathway');
        const pathway = ModuleFE_Pathway.cache.unique(viewedPathwayId);
        const stateTransitions = ModuleFE_PathwayTransition.cache.filter(transition => transition.sourceId === pathwayState._id);
        if (!pathway)
            throw new MUSTNeverHappenException(`Could not get pathway for id ${viewedPathwayId}`);
        const actions = [{ label: 'Edit State', action: this.props.toggleEdit }];
        const isSingleStatePathway = pathway.type === pathwayType_SingleState;
        // Single States pathways and Admit and Discharge states cant have transitions
        if (!isSingleStatePathway && (pathwayState.type && !DispositionTypes.includes(pathwayState.type))) {
            actions.push({
                label: 'Add Transition',
                action: () => {
                    const pathwayState = this.state.pathwayState.item;
                    const pathwayId = viewedPathwayId;
                    const pathwayStateId = pathwayState._id;
                    if (!pathwayId)
                        throw new MUSTNeverHappenException('No set pathway id');
                    if (!pathwayStateId)
                        throw new MUSTNeverHappenException('No set pathway state id');
                    const newTransition = Empty_PathwayTransition(pathwayId, pathwayStateId, '');
                    this.props.functions.selectPathwayTransition(new EditableDBItemV3(newTransition, ModuleFE_PathwayTransition), true);
                }
            });
        }
        if (pathway.type !== pathwayType_SingleState) {
            actions.push({
                label: 'Delete State',
                action: this.deleteState
            });
        }
        if (stateTransitions.length)
            actions.push({
                label: 'Edit Resolution Function',
                action: () => {
                    this.props.functions.selectResolutionFunctionEditor();
                },
            });
        const canEdit = canEditPathway(pathway);
        return _jsxs(_Fragment, { children: [this.renderTopButtons(), _jsx(Header_Pathway, { pathway: new EditableDBItemV3(pathway, ModuleFE_Pathway), allowActions: canEdit, functions: this.props.functions, clickableTitle: true }), _jsx(Header_PathwayState, { pathwayState: this.props.pathwayState, functions: this.props.functions, actions: canEdit ? actions : undefined }), this.renderIsIgnoreDocumentation()] });
    };
    renderTopButtons = () => {
        return _jsx(LL_H_C, { className: 'pathway__top-buttons', children: _jsx(Button, { variant: 'text', className: 'pathway__top-buttons__back', onClick: this.props.functions.deselectPathway, children: '< Back to list' }) });
    };
    renderIsIgnoreDocumentation = () => {
        if (!this.state.pathwayState.item.ignoreDocumentation)
            return;
        return _jsx("div", { className: 'pathway__form__label', children: "This state is ignored in documentation" });
    };
    // ######################## Render - Body ########################
    renderBody = () => {
        return _jsxs(LL_V_L, { className: 'details-renderer__wrapper', children: [_jsxs(DataGrid.Grid, { children: [this.renderLabel(), this.renderSelectedStatePreconditions(), this.renderSelectedStateSmartDoc(), this.renderSelectedStateTransitions()] }), this.renderSelectedStateHCSVariables()] });
    };
    renderLabel = () => {
        const label = this.state.pathwayState.item.label;
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "State Label" }), _jsx(DataGrid.Data, { children: label ?? '-' })] });
    };
    renderSelectedStatePreconditions = () => {
        const preconditions = this.state.pathwayState.item.preconditions ?? [];
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Preconditions" }), _jsxs(LL_V_L, { children: [!preconditions.length && _jsx(DataGrid.Data, { children: "-" }), preconditions.map(precondition => {
                            switch (precondition.itemPointer.dbKey) {
                                case DBDef_Action.dbKey: {
                                    const order = ModuleFE_Action.cache.unique(precondition.itemPointer.id);
                                    return _jsx(DataGrid.Data, { children: order?.name ?? 'N/A' }, precondition.itemPointer.id);
                                }
                                case DBDef_OrderGroup.dbKey: {
                                    const group = ModuleFE_OrderGroup.cache.unique(precondition.itemPointer.id);
                                    return _jsx(DataGrid.Data, { children: group?.name ?? 'N/A' }, precondition.itemPointer.id);
                                }
                                default:
                                    throw new ImplementationMissingException(`No renderer defined for pointer of dbKey ${precondition.itemPointer.dbKey}`);
                            }
                        })] })] });
    };
    renderSelectedStateSmartDoc = () => {
        const pathwayState = this.state.pathwayState.item;
        const smartDoc = ModuleFE_LogTemplate_OLD.cache.unique(pathwayState.logTemplates?.[0]?.templateId);
        return _jsxs(_Fragment, { children: [_jsx(DataGrid.Label, { children: "Smart Doc" }), _jsx(DataGrid.Data, { children: smartDoc?.title ?? '-' })] });
    };
    renderSelectedStateTransitions = () => {
        const pathwayStateType = this.state.pathwayState.item.type;
        // Disposition types cannot have transitions
        if (pathwayStateType && DispositionTypes.includes(pathwayStateType))
            return;
        return _jsx(Section_StateTransitions, { pathwayStateId: this.state.pathwayState.get('_id'), functions: this.props.functions });
    };
    renderSelectedStateHCSVariables = () => {
        return _jsx(Section_AllEntityVariables, { functions: this.props.functions, pathwayStateId: this.state.pathwayState.get('_id') });
    };
}
//# sourceMappingURL=Renderer_PathwayStateDetails.js.map